"use client";

import clsx from "clsx";
import {memo} from "react";
import Icon, {ICON_SIZES, IconNameExtended} from "@/components/library/Icon";

export interface Skill {
   id: string;
   icon: IconNameExtended;
   title: string;
   description: string;
   slug: string;
}

// Shared shell so SkillCard and SkillCardSkeleton always have identical
// dimensions; reserving grid height during load won't drift if we restyle
// the card later.
export const SKILL_CARD_SHELL_CLASS = "flex flex-col gap-2 w-full h-full min-h-[130px] p-4 rounded-xl border border-gpt-border bg-transparent";

interface SkillCardProps {
   skill: Skill;
   onClick: (slug: string) => void;
   disabled?: boolean;
   className?: string;
}

const SkillCard: React.FC<SkillCardProps> = ({skill, onClick, disabled = false, className}) => {
   return (
      <button
         onClick={() => !disabled && onClick(skill.slug)}
         disabled={disabled}
         className={clsx(
            SKILL_CARD_SHELL_CLASS,
            "text-left transition-all duration-200",
            "hover:border-gpt-accent hover:bg-gpt-card",
            "active:scale-[0.97]",
            "focus:outline-none focus:ring-2 focus:ring-[var(--gpt-accent-ring)]",
            disabled && "opacity-50 cursor-not-allowed hover:border-gpt-border hover:bg-transparent active:scale-100",
            className
         )}
      >
         <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gpt-icon-wrap">
            <Icon icon={skill.icon} size={ICON_SIZES.SM} className="text-gpt-text-secondary" />
         </div>
         <div className="font-medium text-sm leading-snug text-900">{skill.title}</div>
         <div className="text-xs font-normal text-500 dark:text-600 leading-relaxed line-clamp-2">{skill.description}</div>
      </button>
   );
};

export const SkillCardSkeleton: React.FC = () => (
   <div className={clsx(SKILL_CARD_SHELL_CLASS, "animate-pulse")} aria-hidden="true">
      <div className="w-9 h-9 rounded-lg bg-gpt-icon-wrap" />
      <div className="h-[18px] w-3/4 rounded bg-gpt-icon-wrap" />
      <div className="h-[40px] w-full rounded bg-gpt-icon-wrap" />
   </div>
);

// The welcome screen re-renders on every composer keystroke.
export default memo(SkillCard);
